import dayjs from 'dayjs'
import { AssignmentWithCourses, AssignmentStatus } from '../types'
import { AssignmentDetail } from '../detail/types'

type AssignmentLike = AssignmentWithCourses | AssignmentDetail

// Assignment Status
export function getAssignmentStatus(
  assignment: AssignmentWithCourses,
): AssignmentStatus {
  if (assignment.status === 'Complete') {
    return 'Completed'
  }

  if (
    assignment.dueDate &&
    dayjs(assignment.dueDate).isBefore(dayjs(), 'day')
  ) {
    return 'Overdue'
  }

  return 'Active'
}

// Date utilities
export function getDaysUntilDue(dueDate: string): number {
  return dayjs(dueDate).diff(dayjs(), 'days')
}

export function getDaysSinceCompleted(dateCompleted: string): number {
  return Math.abs(dayjs().diff(dayjs(dateCompleted), 'days'))
}

export function isOverdue(dueDate: string, isCompleted: boolean): boolean {
  if (!dueDate || isCompleted) return false
  return dayjs(dueDate).isBefore(dayjs(), 'day')
}

export function isWithinDateRange(
  dateStr: string,
  lowerBound: string | null,
  upperBound: string | null,
): boolean {
  let date = dayjs(dateStr)

  if (lowerBound && date.isBefore(dayjs(lowerBound), 'day')) {
    return false
  }

  if (upperBound && date.isAfter(dayjs(upperBound), 'day')) {
    return false
  }

  return true
}

// Course progress calculations (works with both AssignmentWithCourses and AssignmentDetail)
export function getCompletedCourseCount(assignment: AssignmentLike): number {
  return assignment.courses.filter((c) => c.status === 'Complete').length
}

export function getTotalCourseCount(assignment: AssignmentLike): number {
  return assignment.courses.length
}

export function getProgressPercentage(assignment: AssignmentLike): number {
  let total = getTotalCourseCount(assignment)
  if (total === 0) return 0
  let completed = getCompletedCourseCount(assignment)
  return Math.round((completed / total) * 100)
}

// Due date context strings
export function getDueDateContext(assignment: AssignmentWithCourses): string {
  let status = getAssignmentStatus(assignment)

  if (status === 'Completed' && assignment.dateCompleted) {
    let daysSince = getDaysSinceCompleted(assignment.dateCompleted)
    if (daysSince === 0) return 'Completed today'
    if (daysSince === 1) return 'Completed 1 day ago'
    return `Completed ${daysSince} days ago`
  }

  if (status === 'Overdue' && assignment.dueDate) {
    let daysOverdue = Math.abs(getDaysUntilDue(assignment.dueDate))
    if (daysOverdue === 0) return 'Due today'
    if (daysOverdue === 1) return '1 day overdue'
    return `${daysOverdue} days overdue`
  }

  if (status === 'Active' && assignment.dueDate) {
    let daysUntil = getDaysUntilDue(assignment.dueDate)
    if (daysUntil === 0) return 'Due today'
    if (daysUntil === 1) return '1 day until due'
    return `${daysUntil} days until due`
  }

  return ''
}

export function getTimeUntilDue(dueDate: string): string {
  if (!dueDate) return ''

  let now = dayjs()
  let due = dayjs(dueDate)
  let daysDiff = due.diff(now, 'days')

  if (daysDiff < 0) {
    // Overdue
    let daysOverdue = Math.abs(daysDiff)
    if (daysOverdue === 0) return 'Due today'
    if (daysOverdue === 1) return '1 day overdue'
    return `${daysOverdue} days overdue`
  }

  if (daysDiff === 0) {
    return 'Due today'
  }

  if (daysDiff === 1) {
    return '1 day remaining'
  }

  return `${daysDiff} days remaining`
}
